// src/padplanner/PlanPinsOverlay.js
//
// Which plan each dashboard pin is (S6a2, 5g). Over PadPlannerMap's pin
// circles, in a DOM overlay (the PadPlanner style has no glyphs):
//
//   TARGETS   one focusable button per pin, on its circle: hover, keyboard
//             focus or a tap shows the tooltip (plan number, title, status);
//             a click opens the plan. Built when the pins arrive, never on a
//             pan, at most PIN_TARGET_MAX (the perimeters read's own cap); a
//             pin off screen is hidden, and so out of the tab order.
//   LABELS    the plan number beside its pin, from PIN_LABEL_MIN_ZOOM up:
//             lib/map-overlay/screenLabels.js's cull (the FootprintEditor's)
//             with the label's box, so labels never overlap; at most
//             PIN_LABEL_MAX, in a pool built once.
//
// Points are cached when the pins arrive; a map move re-projects them and
// repaints the same elements.
//
// A TAP on a touch screen shows the tooltip first: the first tap on a pin
// says which plan it is, a second tap on the same pin opens it. A mouse
// click opens at once, as the circle's own click did.
import { cullLabels, LabelPool } from "../lib/map-overlay/screenLabels.js";
import { STATUS_LABELS } from "./planVocabulary";

export const PIN_LABEL_MIN_ZOOM = 10;
export const PIN_LABEL_MAX = 150;
export const PIN_TARGET_MAX = 500;
// A label sits right of its pin, vertically centered: its box from the pin's
// point. The text is clipped to it, so the box is what it can cover.
export const PIN_LABEL_BOX = Object.freeze({ dx: 9, dy: -8, w: 108, h: 16 });
const PIN_LABEL_CELL = Object.freeze({ w: 36, h: 16 });
const MARGIN = 24;
const TARGET_PX = 18;

/** "PP-LM-2026-0003 · Pad 3 · In review": what a pin is, in one line. */
export function pinText(p) {
  return [p.plan_number, p.title, STATUS_LABELS[p.status] ?? p.status].filter(Boolean).join(" · ");
}

export default class PlanPinsOverlay {
  constructor({ map, onOpen }) {
    this.map = map;
    this.onOpen = onOpen;
    this.pins = [];
    this.tipFor = null;
    this.tapped = null;
    const container = map.getContainer?.();
    const el = document.createElement("div");
    el.dataset.testid = "pp-pin-overlay";
    Object.assign(el.style, { position: "absolute", inset: "0", pointerEvents: "none", overflow: "hidden" });
    this.labelsEl = document.createElement("div");
    this.targetsEl = document.createElement("div");
    this.tip = document.createElement("div");
    this.tip.id = `pp-pin-tip-${Math.random().toString(36).slice(2, 8)}`;
    this.tip.setAttribute("role", "tooltip");
    this.tip.dataset.testid = "pp-pin-tip";
    Object.assign(this.tip.style, {
      position: "absolute", left: "0", top: "0", display: "none", pointerEvents: "none", zIndex: "2",
      font: "12px/1.35 ui-sans-serif, system-ui, sans-serif", color: "#f8fafc", background: "rgba(15, 23, 42, 0.95)",
      border: "1px solid rgba(148, 163, 184, 0.4)", borderRadius: "4px", padding: "4px 8px", whiteSpace: "nowrap", maxWidth: "280px",
    });
    el.append(this.labelsEl, this.targetsEl, this.tip);
    container?.appendChild(el);
    this.el = el;
    this.labels = new LabelPool(this.labelsEl, { make: makeLabel, size: PIN_LABEL_MAX });
    this.targets = [];

    // One set of listeners on the layer, not one per pin.
    const pinOf = (e) => {
      const b = e.target?.closest?.("[data-plan-id]");
      return b ? this.pins.find((p) => p.id === b.dataset.planId) ?? null : null;
    };
    this._handlers = [
      ["pointerover", (e) => { if (e.pointerType !== "touch") { const p = pinOf(e); if (p) this._showTip(p); } }],
      ["pointerout", (e) => { if (e.pointerType !== "touch" && pinOf(e) && !this.targetsEl.contains(e.relatedTarget)) this._hideTip(); }],
      ["focusin", (e) => { const p = pinOf(e); if (p) this._showTip(p); }],
      ["focusout", () => this._hideTip()],
      ["pointerdown", (e) => {
        if (e.pointerType !== "touch") return;
        const p = pinOf(e);
        // The first tap says which plan; the click that follows it is not an open.
        if (p && this.tipFor !== p.id) { this.tapped = p.id; this._showTip(p); }
      }],
      ["click", (e) => {
        const p = pinOf(e);
        if (!p) return;
        e.stopPropagation();
        if (this.tapped === p.id) { this.tapped = null; return; }
        this.onOpen?.(p.id);
      }],
      ["keydown", (e) => { if (e.key === "Escape") this._hideTip(); }],
    ];
    for (const [ev, fn] of this._handlers) this.targetsEl.addEventListener(ev, fn);
    this._onMove = () => this.render();
    map.on("move", this._onMove);
  }

  destroy() {
    this.map.off?.("move", this._onMove);
    for (const [ev, fn] of this._handlers) this.targetsEl.removeEventListener(ev, fn);
    this.el.remove();
  }

  /** pins: planMapData.pinsFromPerimeters's FeatureCollection, or null. */
  set(fc) {
    this.pins = (fc?.features ?? []).slice(0, PIN_TARGET_MAX).map((f) => ({
      id: f.properties.id, lngLat: f.geometry.coordinates, plan_number: f.properties.plan_number,
      title: f.properties.title, status: f.properties.status,
    }));
    this.targetsEl.replaceChildren();
    this.targets = this.pins.map((p) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "pp-pin-target";
      b.dataset.planId = p.id;
      b.setAttribute("aria-label", `Open ${pinText(p)}`);
      b.setAttribute("aria-describedby", this.tip.id);
      Object.assign(b.style, {
        position: "absolute", left: "0", top: "0", width: `${TARGET_PX}px`, height: `${TARGET_PX}px`, padding: "0",
        borderRadius: "9999px", background: "transparent", border: "0", cursor: "pointer", pointerEvents: "auto",
      });
      this.targetsEl.appendChild(b);
      return b;
    });
    if (this.tipFor && !this.pins.some((p) => p.id === this.tipFor)) this._hideTip();
    this.render();
  }

  /** Re-project and repaint: the targets, the labels, the tooltip. */
  render() {
    const c = this.map.getContainer?.();
    const width = c?.clientWidth ?? 0, height = c?.clientHeight ?? 0;
    const at = this.pins.map((p) => this.map.project(p.lngLat));
    this.targets.forEach((b, i) => {
      const p = at[i];
      const on = p.x >= -MARGIN && p.y >= -MARGIN && p.x <= width + MARGIN && p.y <= height + MARGIN;
      if (!on) { if (b.style.display !== "none") b.style.display = "none"; return; }
      if (b.style.display === "none") b.style.display = "";
      b.style.transform = `translate(${p.x}px, ${p.y}px) translate(-50%, -50%)`;
    });
    this.labels.render(this.visibleLabels({ width, height }), (s, l) => {
      if (s.textContent !== l.text) s.textContent = l.text;
      s.dataset.planId = l.id;
      s.style.transform = `translate(${l.x + PIN_LABEL_BOX.dx}px, ${l.y + PIN_LABEL_BOX.dy}px)`;
    });
    if (this.tipFor) this._placeTip();
  }

  /** The plan-number labels drawn now: [{id, text, x, y}] (x, y the pin's point). */
  visibleLabels({ width, height } = {}) {
    if ((this.map.getZoom?.() ?? 0) < PIN_LABEL_MIN_ZOOM) return [];
    const c = this.map.getContainer?.();
    return cullLabels(this.pins.filter((p) => p.plan_number), {
      project: (ll) => this.map.project(ll), width: width ?? c?.clientWidth ?? 0, height: height ?? c?.clientHeight ?? 0,
      cell: PIN_LABEL_CELL, max: PIN_LABEL_MAX, margin: MARGIN, box: PIN_LABEL_BOX,
    }).map(({ item, x, y }) => ({ id: item.id, text: item.plan_number, x, y }));
  }

  _showTip(p) {
    this.tipFor = p.id;
    this.tip.replaceChildren(...tipLines(p));
    this.tip.style.display = "";
    this._placeTip();
  }

  _hideTip() {
    this.tipFor = null;
    this.tapped = null;
    this.tip.style.display = "none";
  }

  _placeTip() {
    const p = this.pins.find((x) => x.id === this.tipFor);
    if (!p) return;
    const at = this.map.project(p.lngLat);
    this.tip.style.transform = `translate(${at.x}px, ${at.y - 12}px) translate(-50%, -100%)`;
  }
}

function tipLines(p) {
  const line = (text, style) => { const d = document.createElement("div"); d.textContent = text; Object.assign(d.style, style); return d; };
  return [
    line(p.plan_number ?? "Plan", { font: "600 12px/1.35 ui-monospace, SFMono-Regular, Menlo, monospace" }),
    line(p.title ?? "", {}),
    line(STATUS_LABELS[p.status] ?? p.status ?? "", { color: "#94a3b8" }),
  ].map((d, i) => { d.dataset.tipLine = ["plan_number", "title", "status"][i]; return d; });
}

function makeLabel() {
  const s = document.createElement("span");
  s.className = "pp-pin-label";
  Object.assign(s.style, {
    position: "absolute", left: "0", top: "0", height: `${PIN_LABEL_BOX.h}px`, maxWidth: `${PIN_LABEL_BOX.w}px`,
    font: "600 10.5px/16px ui-monospace, SFMono-Regular, Menlo, monospace", color: "#e2e8f0",
    background: "rgba(7, 11, 16, 0.75)", padding: "0 4px", borderRadius: "3px", whiteSpace: "nowrap",
    overflow: "hidden", textOverflow: "ellipsis", boxSizing: "border-box", pointerEvents: "none",
  });
  return s;
}
